import type { WhitelabelConfig } from "@/utils/whitelabel";

/**
 * Whitelabel-aware resolution of the support email / phone / hours to show in
 * an error state, mirroring the pattern in `ApplicationErrorModal` and the
 * `/support` page. Extracted so it can be unit-tested and reused (see
 * `hooks/useSupportContact.ts`).
 *
 * Rules (per whitelabel field):
 * - Whitelabel brand with its own value → use the brand's value.
 * - Whitelabel brand WITHOUT its own value and `showFallbackSupport !== true`
 *   → `null` (the brand has opted out of the platform fallback).
 * - Otherwise (non-whitelabel, or `showFallbackSupport === true`) → the
 *   platform `SUPPORT_CONTACT` fallback.
 */
// Derived from the real brand config (not hand-mirrored) so an upstream field
// rename becomes a compile error here rather than silently resolving every
// brand to the platform fallback. `isWhitelabel` is widened to `boolean` (the
// config union pins it to a `true`/`false` literal per member).
export type WhitelabelSupportSource = Pick<
  WhitelabelConfig,
  | "supportEmail"
  | "supportTelephone"
  | "supportTelephoneHours"
  | "showFallbackSupport"
> & { isWhitelabel: boolean };

export interface SupportContactFallback {
  email: string;
  telephone: string;
  telephoneHours: string;
}

export interface ResolvedSupportContact {
  supportEmail: string | null;
  supportPhone: string | null;
  supportPhoneHours: string | null;
}

export function resolveSupportContact(
  whitelabel: WhitelabelSupportSource,
  fallback: SupportContactFallback
): ResolvedSupportContact {
  const resolve = (
    ownValue: string | null | undefined,
    fallbackValue: string
  ): string | null => {
    if (whitelabel.isWhitelabel && ownValue) return ownValue;
    if (whitelabel.isWhitelabel && whitelabel.showFallbackSupport !== true) {
      return null;
    }
    return fallbackValue;
  };

  return {
    supportEmail: resolve(whitelabel.supportEmail, fallback.email),
    supportPhone: resolve(whitelabel.supportTelephone, fallback.telephone),
    supportPhoneHours: resolve(
      whitelabel.supportTelephoneHours,
      fallback.telephoneHours
    ),
  };
}
